import { useState, useEffect, useCallback, useMemo } from 'react';
import { IoIosAddCircleOutline, IoMdTrash, IoMdCreate } from 'react-icons/io';
import { FaArrowLeft, FaArrowRight, FaUniversity, FaGraduationCap, FaMapMarkerAlt } from 'react-icons/fa';
import styles from './TablaCrud.module.css';
import AniadirUniversidad from './AniadirUniversidad';
import EditarUniversidad from './EditarUniversidad';
import AniadirCarrera from './AniadirCarrera';
import EditarCarrera from './EditarCarrera';
import AniadirPlantel from './AniadirPlantel';
import Swal from 'sweetalert2';
import EditarPlantel from './EditarPlantel';
import {useAuth} from '../../Auth/AuthContext'; 

const CrudUniversidades = () => {
    const [universidades, setUniversidades] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [modalCarreraAbierto, setModalCarreraAbierto] = useState(false); 
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPorPagina] = useState(5);
    const [modalUniversidad, setModalUniversidad] = useState(false);
    const [modalEditarUniversidad, setModalEditarUniversidad] = useState(false); 
    const [modalEditarCarrera, setModalEditarCarrera] = useState(false); 
    const [modalEditarPlantel, setModalEditarPlantel] = useState(false); 
    const [universidadAEditar, setUniversidadAEditar] = useState(null); 
    const [carreraAEditar, setCarreraAEditar] = useState(null); 
    const [plantelAEditar, setPlantelAEditar] = useState(null); 
    const [eliminando, setEliminando] = useState(false);
    const [modalPlantel, setModalPlantel] = useState(false); 
    const [vistaActual, setVistaActual] = useState('universidades');
    const {getToken} = useAuth(); 
    const token = getToken(); 

    const apiCall = useCallback (async (endpoint, options = {}) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/universidades${endpoint}`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers
                },
                ...options
            });
            
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error en API call:', error);
            throw error;
        }
    }, []);

    const cargarDatos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const [universidadesData, carrerasData] = await Promise.all([
                apiCall('/obtener-universidades'),
                apiCall('/obtener-carreras', { 
                    headers: { 
                        'Authorization': `Bearer ${token}`
                    }
                })
            ]);
            
            setUniversidades(universidadesData);
            setCarreras(carrerasData);
            
            const areasUnicas = carrerasData.map(area => ({
                ar_clave: area.id.replace('area-', ''),
                ar_nombre: area.title
            }));
            setAreas(areasUnicas);
            
        } catch (err) {
            console.error('Error cargando datos:', err);
            setError('Error al cargar los datos de la API');
        } finally {
            setLoading(false);
        }
    }, [apiCall]);

    // Funciones para manejar actualizaciones después de editar/crear
    const handleUniversidadCreada = useCallback(async () => {
            try {
                const universidadesData = await apiCall('/obtener-universidades');
                setUniversidades(universidadesData);
            } catch (error) {
                console.error('Error recargando universidades:', error);
            }
    }, [apiCall]);

    const handleUniversidadEditada = useCallback(async () => {
            try {
                const universidadesData = await apiCall('/obtener-universidades');
                setUniversidades(universidadesData);
            } catch (error) {
                console.error('Error recargando universidades:', error);
            }
    }, [apiCall]);

    const handleCarreraCreada = useCallback(async () => {
            try {
                const carrerasData = await apiCall('/obtener-carreras', { 
                    headers: { 
                        'Authorization': `Bearer ${token}`
                    }
                });
                setCarreras(carrerasData);
            } catch (error) {
                console.error('Error recargando carreras:', error);
            }
    }, [apiCall]);

    const handleCarreraEditada = useCallback(async () => {
            try {
                const carrerasData = await apiCall('/obtener-carreras', { 
                    headers: { 
                        'Authorization': `Bearer ${token}`
                    }
                });
                setCarreras(carrerasData);
            } catch (error) {
                console.error('Error recargando carreras:', error);
            }
    }, [apiCall]);

    const handlePlantelCreado = useCallback(async () => {
            try {
                const universidadesData = await apiCall('/obtener-universidades');
                setUniversidades(universidadesData);
            } catch (error) {
                console.error('Error recargando universidades:', error);
            }
    }, [apiCall]);

    const handlePlantelEditado = useCallback(async () => {
            try {
                const universidadesData = await apiCall('/obtener-universidades');
                setUniversidades(universidadesData);
            } catch (error) {
                console.error('Error recargando universidades:', error);
            }
    }, [apiCall]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    const itemsFiltrados = useMemo(() => {
        const searchTermLower = searchTerm.toLowerCase();
        
        switch (vistaActual) {
            case 'universidades':
                return universidades.filter((uni) =>
                    uni.name?.toLowerCase().includes(searchTermLower)
                );
            case 'carreras':
                return carreras.flatMap(area => 
                    area.careers?.filter(carrera =>
                        carrera.name?.toLowerCase().includes(searchTermLower)
                    ).map(carrera => ({
                        ...carrera,
                        area_nombre: area.title
                    })) || []
                );
            case 'planteles':
                return universidades.flatMap(uni =>
                    uni.planteles?.map(plantel => ({
                        ...plantel,
                        universidad_nombre: uni.name,
                        universidad_id: uni.id
                    })) || []
                ).filter(plantel =>
                    plantel.universidad_nombre?.toLowerCase().includes(searchTermLower) ||
                    plantel.address?.toLowerCase().includes(searchTermLower)
                );
            default:
                return [];
        }
    }, [vistaActual, searchTerm, universidades, carreras]);

    const itemsActuales = useMemo(() => {
        const indexOfLastItem = currentPage * itemsPorPagina;
        const indexOfFirstItem = indexOfLastItem - itemsPorPagina;
        return itemsFiltrados.slice(indexOfFirstItem, indexOfLastItem);
    }, [itemsFiltrados, currentPage, itemsPorPagina]);

    const paginationInfo = useMemo(() => {
        const indexOfLastItem = currentPage * itemsPorPagina;
        const indexOfFirstItem = indexOfLastItem - itemsPorPagina;
        return { indexOfFirstItem, indexOfLastItem };
    }, [currentPage, itemsPorPagina]);

    const confirmarEliminacion = useCallback((universidad) => {
            Swal.fire({
                title: `¿Eliminar ${universidad.name}?`,
                text: 'Esta acción eliminará también todos los planteles asociados y no se puede deshacer.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: eliminando ? 'Eliminando...' : 'Eliminar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#242b57',
                cancelButtonColor: '#6c757d',
                customClass: {
                    popup: 'popupbonito'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    eliminarUniversidad(universidad);
                }
            });
        }, [eliminando]);

    const eliminarUniversidad = useCallback(async (universidadAEliminar) => {
        if (!universidadAEliminar) {
            await Swal.fire({
                title: 'Error',
                text: 'No se ha seleccionado ninguna universidad para eliminar.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
            setEliminando(false);
            return;
        }
        
        const universidadId = universidadAEliminar.id || universidadAEliminar.un_clave;

        if (!universidadId) {
            await Swal.fire({
                title: 'Error',
                text: 'No se pudo identificar la universidad a eliminar.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
            setEliminando(false);
            return;
        }

        try {
            setEliminando(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/eliminar-universidad/${universidadId}`, {
                method: 'DELETE', 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
        }); 

        if(!response.ok) { 
            throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json(); 

            setUniversidades(prev => 
                prev.filter(uni => {
                    const uniId = uni.id || uni.un_clave;
                    return uniId !== universidadId;
                })
            );
            await Swal.fire({
                title: 'Universidad eliminada',
                text: `La universidad "${universidadAEliminar.name}" ha sido eliminada exitosamente.`,
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });

        } catch (error) {
            console.error('Error al eliminar universidad:', error);
            await Swal.fire({
                title: 'Error',
                text: `No se pudo eliminar la universidad: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
        } finally {
            setEliminando(false);
        }
    }, [apiCall]);

    const eliminarCarrera = useCallback(async (carrera) => { 
        if (!carrera || (!carrera.id && !carrera.ca_clave)) {
            await Swal.fire({
                title: 'Error',
                text: 'No se ha seleccionado ninguna carrera para eliminar.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
            setEliminando(false);
            return;
        }

        try {
            setEliminando(true);
            const carreraId = carrera.id || carrera.ca_clave;

            await fetch(`${process.env.REACT_APP_API_URL}/admin/eliminar-carrera/${carreraId}`, {
                method: 'DELETE', 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            setCarreras(prev => 
                prev.map(area => ({
                    ...area,
                    careers: area.careers?.filter(c => {
                        const carrId = c.id || c.ca_clave;
                        const eliminarId = carrera.id || carrera.ca_clave;
                        return carrId !== eliminarId;
                    }) || []
                }))
            );

            await Swal.fire({
                title: 'Carrera eliminada',
                text: `La carrera "${carrera.name}" ha sido eliminada exitosamente.`,
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });

        } catch (error) {
            console.error('Error al eliminar carrera:', error);
            await Swal.fire({
                title: 'Error',
                text: `No se pudo eliminar la carrera: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
        } finally {
            setEliminando(false);
        }
    }, [apiCall]);

    const confirmarCarreraEliminacion = useCallback((carrera) => { 
        Swal.fire({
            title: `¿Eliminar ${carrera.name}?`,
            text: 'Esta acción eliminará también todas las relaciones con universidades y no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: eliminando ? 'Eliminando...' : 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#242b57',
            cancelButtonColor: '#6c757d',
            customClass: {
                popup: 'popupbonito'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                eliminarCarrera(carrera);
            }
        });
    }, [eliminando, eliminarCarrera]);

    const eliminarPlantel = useCallback(async (plantel) => {
        if (!plantel || !plantel.id) {
            await Swal.fire({
                title: 'Error',
                text: 'No se ha seleccionado ningún plantel para eliminar.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
            setEliminando(false);
            return;
        }

        try {
            setEliminando(true);

            await fetch(`${process.env.REACT_APP_API_URL}/admin/eliminar-plantel/${plantel.id}`, {
                method: 'DELETE', 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            setUniversidades(prev =>
                prev.map(uni => ({
                    ...uni,
                    planteles: uni.planteles?.filter(p => p.id !== plantel.id) || []
                }))
            );

            await Swal.fire({
                title: 'Plantel eliminado',
                text: `El plantel ha sido eliminado exitosamente.`,
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });

        } catch (error) {
            console.error('Error al eliminar plantel:', error);
            await Swal.fire({
                title: 'Error',
                text: `No se pudo eliminar el plantel: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
        } finally {
            setEliminando(false);
        }
    }, [apiCall]);

    const confirmarPlantelEliminacion = useCallback((plantel) => {
        Swal.fire({
            title: `¿Eliminar plantel?`,
            text: `Esta acción eliminará el plantel en "${plantel.address || 'Sin dirección'}" y no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: eliminando ? 'Eliminando...' : 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#242b57',
            cancelButtonColor: '#6c757d',
            customClass: {
                popup: 'popupbonito'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                eliminarPlantel(plantel);
            }
        });
    }, [eliminando, eliminarPlantel]);

    const abrirModalEditar = useCallback((universidad) => { 
        setUniversidadAEditar(universidad.id); 
        setModalEditarUniversidad(true); 
    }, []); 

    const cerrarModalEditar = useCallback(() => { 
        setModalEditarUniversidad(false); 
        setUniversidadAEditar(null); 
    }, []);

    const cerrarModalCarrera = useCallback(() => { 
        setModalCarreraAbierto(false); 
    }, []);

    const abrirModalEditarCarrera = useCallback((carrera) => { 
        setCarreraAEditar(carrera.id); 
        setModalEditarCarrera(true);
    }, []);

    const cerrarModalEditarCarrera = useCallback(() => { 
        setModalEditarCarrera(false);
        setCarreraAEditar(null);
    }, []);

    const abrirModalEditarPlantel = useCallback((plantel) => { 
        setPlantelAEditar(plantel.id); 
        setModalEditarPlantel(true); 
    }, []);

    const cerrarModalEditarPlantel = useCallback(() => {
        setModalEditarPlantel(false);
        setPlantelAEditar(null); 
    }, []);

    const paginate = useCallback((pageNumber) => setCurrentPage(pageNumber), []);

    const TablaCarreras = () => (
        <div className={styles.tablaOverflow}>
            <table className={styles.tablaUsuarios}>
                <thead className={styles.tablaHeader}>
                    <tr>
                        <th>Nombre</th>
                        <th>Área</th>
                        <th>Universidades</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {itemsActuales.length > 0 ? (
                        itemsActuales.map((carrera) => (
                            <tr key={carrera.id} className={styles.tablaFila}>
                                <td className={styles.tablaCelda}>{carrera.name}</td>
                                <td className={styles.tablaCelda}>{carrera.area_nombre}</td>
                                <td className={styles.tablaCelda}>{carrera.universities?.length || 0}</td>
                                <td className={styles.tablaCelda}>
                                    <div className={styles.accionesContainer}>
                                        <button className={`${styles.botonAccion} ${styles.editar}`}
                                                onClick = {() => abrirModalEditarCarrera(carrera)}>
                                            <IoMdCreate />
                                        </button>
                                        <button className={`${styles.botonAccion} ${styles.eliminar}`}
                                                onClick={() => confirmarCarreraEliminacion(carrera)}
                                                disabled={eliminando}>
                                            <IoMdTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="5" className={styles.tablaNoResultados}>
                                No se encontraron carreras
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const TablaPlanteles = () => (
        <div className={styles.tablaOverflow}>
            <table className={styles.tablaUsuarios}>
                <thead className={styles.tablaHeader}>
                    <tr>
                        <th>Universidad</th>
                        <th>Dirección</th>
                        <th>Teléfono</th>
                        <th>Correo</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {itemsActuales.length > 0 ? (
                        itemsActuales.map((plantel) => (
                            <tr key={`${plantel.universidad_id}-${plantel.id}`} className={styles.tablaFila}>
                                <td className={styles.tablaCelda}>{plantel.universidad_nombre}</td>
                                <td className={styles.tablaCelda}>{plantel.address || 'Sin dirección'}</td>
                                <td className={styles.tablaCelda}>{plantel.phone?.join(', ') || 'Sin teléfono'}</td>
                                <td className={styles.tablaCelda}>{plantel.email?.join(', ') || 'Sin correo'}</td>
                                <td className={styles.tablaCelda}>
                                    <div className={styles.accionesContainer}>
                                        <button className={`${styles.botonAccion} ${styles.editar}`}
                                                onClick = {() => abrirModalEditarPlantel(plantel)}>
                                            <IoMdCreate />
                                        </button>
                                        <button className={`${styles.botonAccion} ${styles.eliminar}`}
                                                onClick = { () => confirmarPlantelEliminacion(plantel)}
                                                disabled={eliminando}>
                                            <IoMdTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className={styles.tablaNoResultados}>
                                No se encontraron planteles
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando datos...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorAlert} role="alert">
                <strong>Error: </strong>
                <span>{error}</span>
            </div>
        );
    }

    return (
        <div className={styles.tablaContainer}>
            <h2 className={styles.tablaTitulo}>Gestión de Universidades</h2>

            <div className={styles.navTabs}>
                <button 
                    className={`${styles.navTab} ${vistaActual === 'universidades' ? styles.active : ''}`}
                    onClick={() => {
                        setVistaActual('universidades');
                        setCurrentPage(1);
                        setSearchTerm('');
                    }}
                >
                    <FaUniversity /> Universidades ({universidades.length})
                </button>
                <button 
                    className={`${styles.navTab} ${vistaActual === 'carreras' ? styles.active : ''}`}
                    onClick={() => {
                        setVistaActual('carreras');
                        setCurrentPage(1);
                        setSearchTerm('');
                    }}
                >
                    <FaGraduationCap /> Carreras ({carreras.flatMap(a => a.careers || []).length})
                </button>
                <button 
                    className={`${styles.navTab} ${vistaActual === 'planteles' ? styles.active : ''}`}
                    onClick={() => {
                        setVistaActual('planteles');
                        setCurrentPage(1);
                        setSearchTerm('');
                    }}
                >
                    <FaMapMarkerAlt /> Planteles ({universidades.flatMap(u => u.planteles || []).length})
                </button>
            </div>

            <div className={styles.searchExportContainer}>
                <div className={styles.searchContainer}>
                    <input
                        type="text"
                        placeholder={`Buscar ${vistaActual}...`}
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>
                <div className={styles.buttonContainer}>
                    {vistaActual === 'universidades' && (
                        <button className={styles.botonAdd} onClick={() => setModalUniversidad(true)}>
                            <IoIosAddCircleOutline />
                            Añadir Universidad
                        </button>
                    )}
                    <AniadirUniversidad
                        abrirModal={modalUniversidad}
                        cerrarModal={() => setModalUniversidad(false)}
                        onUniversidadCreada={handleUniversidadCreada}
                    />

                    {vistaActual === 'carreras' && (
                        <button className={styles.botonAdd} onClick={() => setModalCarreraAbierto(true)}>
                            <IoIosAddCircleOutline />
                            Añadir Carrera
                        </button>
                    )}
                    {vistaActual === 'planteles' && (
                        <button className={styles.botonAdd} onClick={() => setModalPlantel(true)}>
                            <IoIosAddCircleOutline />
                            Añadir Plantel
                        </button>
                    )}
                </div>
            </div>

            {vistaActual === 'universidades' && (
                <div className={styles.tablaOverflow}>
                    <table className={styles.tablaUsuarios}>
                        <thead className={styles.tablaHeader}>
                            <tr>
                                <th>Nombre</th>
                                <th>Logo</th>
                                <th>Página Web</th>
                                <th>Planteles</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itemsActuales.length > 0 ? (
                                itemsActuales.map((universidad) => (
                                    <tr key={universidad.id} className={styles.tablaFila}>
                                        <td className={styles.tablaCelda}>{universidad.name}</td>
                                        <td className={styles.tablaCelda}>
                                            {universidad.logo ? (
                                                <img 
                                                    src={`${process.env.REACT_APP_API_URL}/universidades/logos-universidades/${universidad.logo}`} 
                                                    alt="Logo" 
                                                    className={styles.logoImg} 
                                                />
                                            ) : (
                                                'Sin logo'
                                            )}
                                        </td>
                                        <td className={styles.tablaCelda}>
                                            {universidad.contact?.website ? (
                                                <a href={universidad.contact.website} target="_blank" rel="noopener noreferrer">
                                                    {universidad.contact.website}
                                                </a>
                                            ) : (
                                                'Sin página'
                                            )}
                                        </td>
                                        <td className={styles.tablaCelda}>{universidad.planteles?.length || 0}</td>
                                        <td className={styles.tablaCelda}>
                                            <div className={styles.accionesContainer}>
                                                <button
                                                    className={`${styles.botonAccion} ${styles.editar}`}
                                                    onClick = {() => abrirModalEditar(universidad)}>
                                                    <IoMdCreate />
                                                </button>
                                                <button
                                                    className={`${styles.botonAccion} ${styles.eliminar}`}
                                                    onClick={() => confirmarEliminacion(universidad)}
                                                    disabled={eliminando}
                                                >
                                                    <IoMdTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className={styles.tablaNoResultados}>
                                        No se encontraron universidades
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {vistaActual === 'carreras' && <TablaCarreras />}
            {vistaActual === 'planteles' && <TablaPlanteles />}

            {itemsFiltrados.length > 0 && (
                <div className={styles.paginacionContainer}>
                    <div>
                        <p className={styles.paginacionInfo}>
                            Mostrando {paginationInfo.indexOfFirstItem + 1} - {Math.min(paginationInfo.indexOfLastItem, itemsFiltrados.length)} de {itemsFiltrados.length} elementos
                        </p>
                    </div>
                    <div className={styles.paginacionControles}>
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`${styles.paginacionBoton} ${currentPage === 1 ? styles.deshabilitado : styles.primario}`}
                        >
                            <FaArrowLeft />
                        </button>
                        <select
                            className={styles.paginacionSelect}
                            value={currentPage}
                            onChange={(e) => paginate(Number(e.target.value))}
                        >
                            {Array.from({ length: Math.ceil(itemsFiltrados.length / itemsPorPagina) }).map((_, index) => (
                                <option key={index} value={index + 1}>
                                    {index + 1}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === Math.ceil(itemsFiltrados.length / itemsPorPagina)}
                            className={`${styles.paginacionBoton} ${
                                currentPage === Math.ceil(itemsFiltrados.length / itemsPorPagina)
                                    ? styles.deshabilitado
                                    : styles.primario
                            }`}
                        >
                            <FaArrowRight />
                        </button>
                    </div>
                </div>
            )}

            <EditarUniversidad
                abrirModal = {modalEditarUniversidad}
                cerrarModal = {cerrarModalEditar} 
                universidadId = {universidadAEditar}
                onUniversidadEditada={handleUniversidadEditada}
            />
            <AniadirCarrera
                abrirModal={modalCarreraAbierto}
                cerrarModal={cerrarModalCarrera}
                onCarreraCreada={handleCarreraCreada}
            /> 
            <EditarCarrera
                abrirModal={modalEditarCarrera}
                cerrarModal={cerrarModalEditarCarrera}
                carreraId={carreraAEditar}
                onCarreraEditada={handleCarreraEditada}
            />

            <AniadirPlantel 
                abrirModal={modalPlantel}
                cerrarModal={() => setModalPlantel(false)}
                onPlantelCreado={handlePlantelCreado}
            />

            <EditarPlantel 
                abrirModal={modalEditarPlantel}
                cerrarModal={cerrarModalEditarPlantel}
                plantelId={plantelAEditar}
                onPlantelEditado={handlePlantelEditado}
            /> 

        </div>
    );
};

export default CrudUniversidades;